import React, { useState } from 'react';
import { LogoNeural, LogoBrain, LogoDataFlow, LogoHexTech, LogoPulse, LogoHub } from '../components/Logo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';

const LogoPreview = () => {
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [animate, setAnimate] = useState(true);

  const logos = [
    { 
      id: 'neural', 
      name: 'Neural Network', 
      description: 'AI brain with connected neurons - shows intelligence & connectivity',
      Component: LogoNeural,
      colors: 'Blue → Purple → Green'
    },
    { 
      id: 'brain', 
      name: 'Brain Circuit', 
      description: 'Tech brain with circuit paths - AI processing & intelligence',
      Component: LogoBrain,
      colors: 'Indigo → Purple → Pink'
    },
    { 
      id: 'dataflow', 
      name: 'Data Flow Waves', 
      description: 'Dynamic waves with center core - data streaming & flow',
      Component: LogoDataFlow,
      colors: 'Cyan → Indigo → Purple'
    },
    { 
      id: 'hextech', 
      name: 'Hex Tech AI', 
      description: 'Hexagonal tech design with AI text - modern & geometric',
      Component: LogoHexTech,
      colors: 'Teal → Blue → Purple'
    },
    { 
      id: 'pulse', 
      name: 'Pulse Signal', 
      description: 'Heartbeat/signal line with rings - alive & active system',
      Component: LogoPulse,
      colors: 'Rose → Purple → Blue'
    },
    { 
      id: 'hub', 
      name: 'Network Hub', 
      description: 'Connected nodes to center - connectivity & network',
      Component: LogoHub,
      colors: 'Amber → Red → Pink'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            🎨 FlowFeed ERP Logo Options
          </h1>
          <p className="text-gray-400 text-lg mb-6">
            AI Connectivity with Pulse Animation - Choose your favorite!
          </p>
          <Button 
            onClick={() => setAnimate(!animate)}
            variant={animate ? "default" : "outline"}
            className="mb-4"
          >
            Animation: {animate ? 'ON' : 'OFF'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {logos.map((logo) => {
            const LogoComponent = logo.Component;
            const isSelected = selectedLogo === logo.id;
            
            return (
              <Card 
                key={logo.id}
                className={`cursor-pointer transition-all duration-300 hover:scale-105 ${
                  isSelected 
                    ? 'ring-4 ring-blue-500 bg-blue-950/50' 
                    : 'bg-gray-800/50 hover:bg-gray-700/50'
                }`}
                onClick={() => setSelectedLogo(logo.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-lg">
                      Option {logos.indexOf(logo) + 1}: {logo.name}
                    </CardTitle>
                    {isSelected && (
                      <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Logo Preview */}
                  <div className="flex justify-center items-center py-8 bg-gray-900/50 rounded-lg mb-4">
                    <LogoComponent size={80} animate={animate} />
                  </div>
                  
                  {/* Logo with text preview */}
                  <div className="flex items-center gap-3 p-4 bg-gray-900/30 rounded-lg mb-4">
                    <LogoComponent size={40} animate={animate} />
                    <div>
                      <div className="font-bold text-white">FlowFeed ERP</div>
                      <div className="text-[10px] text-blue-400 uppercase tracking-widest">Neural Business Suite</div>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-sm mb-2">{logo.description}</p>
                  <p className="text-xs text-gray-500">Colors: {logo.colors}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected Logo Preview */}
        {selectedLogo && (
          <div className="bg-gray-800/50 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-6">Selected: {logos.find(l => l.id === selectedLogo)?.name}</h2>
            <div className="flex justify-center items-center gap-8 flex-wrap">
              {/* Small */}
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Small (32px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo)?.Component, { size: 32, animate })}
              </div>
              {/* Medium */}
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Medium (48px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo)?.Component, { size: 48, animate })}
              </div>
              {/* Large */}
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Large (80px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo)?.Component, { size: 80, animate })}
              </div>
              {/* XL */}
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">XL (120px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo)?.Component, { size: 120, animate })}
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-white rounded-lg inline-block">
              <div className="flex items-center gap-3">
                {React.createElement(logos.find(l => l.id === selectedLogo)?.Component, { size: 40, animate })}
                <div className="text-left">
                  <div className="font-bold text-gray-900">FlowFeed ERP</div>
                  <div className="text-[10px] text-blue-600 uppercase tracking-widest">Neural Business Suite</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Tell me which logo number you prefer and I'll update it across the application!</p>
          <p className="mt-2">Options: 1 (Neural), 2 (Brain), 3 (DataFlow), 4 (HexTech), 5 (Pulse), 6 (Hub)</p>
        </div>
      </div>
    </div>
  );
};

export default LogoPreview;
